import { makeAutoObservable, runInAction } from 'mobx';
import { isNumber } from '@cutwise/utils';

import useStore from '@/common/hooks/useStore';
import Notifier from '@/common/services/Notifier';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { GlobalAccessTarget } from '@/security/entities/GlobalAccessTarget';
import { EntityAccessChange, EntityAccessEventEmitter } from '@/security/services/EntityAccessEventEmitter';
import { AccessTargetType } from '@/security/types/AccessTarget';
import { ProductDataAccess } from '@/security/types/DataAccess';
import { GlobalAccessRequest, GlobalAccessResponse, GlobalAccessValues } from '@/security/types/GlobalAccess';

import SecurityAPI from '../../api/SecurityAPI';

export class GlobalAccessModalStore {
  private readonly targets: GlobalAccessTarget[] = [];

  private targetsValues: GlobalAccessValues[] = [];

  recursive = false;

  isUpdating = false;

  isFetching = false;

  type: AccessTargetType;

  private readonly securityAPI: SecurityAPI;

  constructor(
    targets: GlobalAccessTarget[],
    type: AccessTargetType,
    private readonly entityAccessEventEmitter: EntityAccessEventEmitter,
  ) {
    makeAutoObservable<GlobalAccessModalStore, 'securityAPI' | 'toAccessRequest' | 'entityAccessEventEmitter'>(this, {
      securityAPI: false,
      toAccessRequest: false,
      entityAccessEventEmitter: false,
    });

    this.type = type;
    this.targets = targets;

    // we don't need the whole structure when we change data
    this.targetsValues = this.getAccessValues(targets);

    this.securityAPI = new SecurityAPI();
  }

  get accessValues() {
    return this.targetsValues.map((target) => target.access);
  }

  get dataAccessValues() {
    return this.targetsValues.map((target) => target.dataAccess);
  }

  get accessDiff() {
    return (
      this.targetsValues.filter(({ id, access }) => this.targets.find((target) => target.id === id)?.access !== access)
        .length > 0
    );
  }

  get dataAccessDiff() {
    return (
      this.targetsValues.filter(
        ({ id, dataAccess }) => this.targets.find((target) => target.id === id)?.dataAccess !== dataAccess,
      ).length > 0
    );
  }

  get entitiesCount() {
    return this.targets.length;
  }

  get shouldShowDataAccess() {
    return this.targets.every((target) => target.hasDataAccess);
  }

  setAccess = (nextAccessLevel: GlobalAccessLevel) => {
    this.targetsValues = this.targetsValues.map((target) => {
      target.access = nextAccessLevel;

      return target;
    });
  };

  setDataAccess = (nextDataAccessLevel: ProductDataAccess) => {
    this.targetsValues = this.targetsValues.map((target) => {
      target.dataAccess = nextDataAccessLevel;

      return target;
    });
  };

  setRecursive = (state: boolean) => {
    this.recursive = state;
  };

  updateAccessList = async (): Promise<EntityAccessChange[]> => {
    this.isUpdating = true;

    try {
      const response = await this.securityAPI.updateAccess(this.targetsValues.map(this.toAccessRequest));

      const successUpdate: GlobalAccessResponse[] = [];
      const failUpdate: GlobalAccessResponse[] = [];

      (response?.content ?? []).forEach((entity: GlobalAccessResponse) => {
        if (entity.state === 'ok') {
          successUpdate.push(entity);
        } else {
          failUpdate.push(entity);
        }
      });

      if (successUpdate.length > 0 && failUpdate.length === 0) {
        Notifier.success(
          `${this.type === 'project' || this.type === 'driveProject' ? 'Project' : 'Product'} access updated`,
        );
      }

      if (failUpdate.length > 0) {
        if (successUpdate.length > 0) {
          Notifier.warn(`${failUpdate.length} failed to update`);
        } else if (failUpdate.length === 1 && failUpdate?.[0]?.error) {
          Notifier.error(failUpdate[0].error);
        } else {
          Notifier.error(`${failUpdate.length} failed to update`);
        }
      }

      runInAction(() => {
        this.isUpdating = false;
      });

      const changes = successUpdate.map(({ id, access, dataAccess }) => ({ id, access, dataAccess }));

      this.entityAccessEventEmitter.emitAccessChange(this.type, changes);

      return changes;
    } catch (e) {
      Notifier.error(e);

      runInAction(() => {
        this.isUpdating = false;
      });

      return [];
    }
  };

  reset = () => {
    this.setRecursive(false);
    this.targetsValues = this.getAccessValues(this.targets);
  };

  private toAccessRequest = (values: GlobalAccessValues): GlobalAccessRequest => {
    const request: GlobalAccessRequest = {
      id: values.id,
      access: values.access,
      type: this.type,
    };

    if (isNumber(values.dataAccess)) {
      request.dataAccess = values.dataAccess;
    }

    if (this.type === 'project') {
      request.recursive = this.recursive;
    }

    return request;
  };

  private getAccessValues(targets: GlobalAccessTarget[]) {
    return targets.map((target) => ({
      id: target.id,
      access: target.access,
      dataAccess: target.dataAccess,
    }));
  }
}

export const useGlobalAccessModalStore = () => useStore(GlobalAccessModalStore);
